import { useCallback, useId, useMemo, useRef, useState } from "react";
import { cn } from "../lib/utils";
import { Compteur, Enregistrement, type EtatCompteur, useCompteur } from "./bobines-compteur";
import type { Reglage } from "./bobines-requetes";
import { BoutonAjoutReserve, ChoixReserve } from "./bobines-reserve";
import {
  aRacheterEnClair,
  COMPTEURS,
  correspond,
  estBambu,
  etatEnClair,
  etatsCourts,
  etatsEnClair,
  grammes,
  LEGENDE_RESERVE,
  nomComplet,
} from "./bobines-textes";
import { CARTE, Chevron, FOCUS, Jauge, Pastille } from "./bobines-ui";
import type { GammeVue, ReferenceVue, VueBobines } from "./contract";
import { compter } from "./format";

/**
 * Le stock, sous la liste de courses (spec, section 4, et son amendement du 28/09/2026 ;
 * maquettes `Bobines.dc.html` et `BobinesMobile.dc.html`) : le bouton « Ajouter une couleur en
 * reserve » et son choix (`bobines-reserve.tsx`), un filtre, la legende de la reserve, puis un
 * groupe repliable par gamme de Bambu et un dernier pour les autres marques. Un groupe s'ouvre
 * d'office quand il a quelque chose a racheter ou qu'un filtre est tape ; ferme, il montre ses
 * teintes en petites pastilles. Une ligne par reference : pastille, nom, jauges, etats, grammes,
 * reserve et minimum ; un clic sur le nom deplie ses bobines.
 *
 * La mise en page d'une ligne suit la largeur de la carte, pas celle de l'ecran (`@container`),
 * en trois dispositions : sous 20rem (`@xs`, un telephone de 320 ou 360 px), les deux compteurs
 * sur leur propre rang sous le nom ; de 20 a 42rem (un telephone de 390 px, et la carte de
 * 360 px d'un ecran de 1 024 px a cote de la liste de courses), l'un sous l'autre a droite,
 * chacun nomme ; au-dela de 42rem (`@2xl`), les colonnes de la maquette du bureau, plus celle de
 * la reserve.
 *
 * **Rien ne bouge sous le clic d'un compteur** : l'ordre des gammes et des lignes et les groupes
 * ouverts d'office sont figes a la premiere lecture (`depart`), et une reference sans bobine
 * remise a 0 de minimum et de reserve reste a l'ecran jusqu'au rechargement (`retenir`,
 * `stabiliser`). Le bouton clique, lui, reste a sa place a l'ecran pendant l'envoi
 * (`bobines-compteur.tsx`).
 */

/** Un groupe du stock : une gamme de Bambu, ou toutes les gammes des autres marques. */
export type GroupeStock = {
  readonly cle: string;
  readonly titre: string;
  readonly gammes: readonly GammeVue[];
  readonly autres: boolean;
};

/** La cle d'une gamme : sa marque et son nom (« PLA Basic » existe chez plusieurs marques). */
function cleGamme(gamme: Pick<GammeVue, "marque" | "gamme">): string {
  return JSON.stringify([gamme.marque, gamme.gamme]);
}

/** Le groupe du stock ou tombe une gamme : le sien chez Bambu, « Autres marques » sinon. */
const AUTRES_MARQUES = "autres-marques";
export function groupeDe(gamme: Pick<GammeVue, "marque" | "gamme">): string {
  return estBambu(gamme.marque) ? cleGamme(gamme) : AUTRES_MARQUES;
}

/** Les gammes de Bambu dans l'ordre de l'API, puis les autres marques reunies, en dernier. */
export function groupesDuStock(gammes: readonly GammeVue[]): GroupeStock[] {
  const autres = gammes.filter((gamme) => !estBambu(gamme.marque));
  return [
    ...gammes
      .filter((gamme) => estBambu(gamme.marque))
      .map((gamme) => ({
        cle: groupeDe(gamme),
        titre: gamme.gamme,
        gammes: [gamme],
        autres: false,
      })),
    ...(autres.length === 0
      ? []
      : [{ cle: AUTRES_MARQUES, titre: "Autres marques", gammes: autres, autres: true }]),
  ];
}

/** Le groupe reduit aux references qui passent le filtre ; null s'il n'en garde aucune. */
function filtrer(groupe: GroupeStock, filtre: string): GroupeStock | null {
  const gammes = groupe.gammes
    .map((gamme) => ({
      ...gamme,
      references: gamme.references.filter((reference) => correspond(reference, filtre)),
    }))
    .filter((gamme) => gamme.references.length > 0);
  return gammes.length === 0 ? null : { ...groupe, gammes };
}

function aRacheter(groupe: GroupeStock): boolean {
  return groupe.gammes.some((gamme) => gamme.references.some((ref) => ref.a_racheter > 0));
}

/**
 * Ce que la page fige a sa premiere lecture : le rang de chaque reference et de chaque gamme,
 * et les groupes ouverts d'office. Sans cela, le minimum bougeait la page sous le clic : la
 * relecture qui le suit retrie les references (celles a racheter d'abord) et les gammes (par
 * nombre de bobines), une ligne ou un groupe changeait de rang et le clic suivant tombait sur
 * sa voisine ; un groupe ouvert parce qu'il avait quelque chose a racheter se refermait des que
 * son dernier manque etait comble, et le focus tombait sur la page. Un rechargement reprend
 * l'ordre du backend.
 */
export type Depart = {
  /** Le rang de chaque reference, par sa cle. */
  readonly rangs: ReadonlyMap<string, number>;
  /** Le rang de chaque gamme, par sa marque et son nom (`cleGamme`). */
  readonly rangsGammes: ReadonlyMap<string, number>;
  readonly ouverts: ReadonlySet<string>;
};

export function depart(gammes: readonly GammeVue[]): Depart {
  const cles = gammes.flatMap((gamme) => gamme.references.map((reference) => reference.cle));
  return {
    rangs: new Map(cles.map((cle, rang) => [cle, rang])),
    rangsGammes: new Map(gammes.map((gamme, rang) => [cleGamme(gamme), rang])),
    ouverts: new Set(
      groupesDuStock(gammes)
        .filter(aRacheter)
        .map((groupe) => groupe.cle),
    ),
  };
}

/**
 * Les references suivies sans bobine que la derniere relecture a fait disparaitre : leur
 * minimum et leur reserve viennent de passer tous deux a 0 (sans bobine, c'est la seule facon
 * de quitter la vue). Elles restent a l'ecran a 0 jusqu'au rechargement, pour qu'un « - » de
 * trop se rattrape par un « + » ; la table des minimums les connait encore, le `PUT` passe.
 */
export function retenir(
  avant: VueBobines,
  apres: VueBobines,
  retenues: ReadonlyMap<string, ReferenceVue>,
): ReadonlyMap<string, ReferenceVue> {
  const presentes = new Set(apres.gammes.flatMap((g) => g.references.map((r) => r.cle)));
  const suivantes = new Map([...retenues].filter(([cle]) => !presentes.has(cle)));
  for (const reference of avant.gammes.flatMap((gamme) => gamme.references)) {
    if (reference.bobines.length === 0 && !presentes.has(reference.cle)) {
      suivantes.set(reference.cle, { ...reference, minimum: 0, reserve: 0, a_racheter: 0 });
    }
  }
  return suivantes;
}

/**
 * Les gammes a montrer : celles de la vue, plus les references retenues (dans leur gamme, ou
 * dans leur gamme recreee si elle n'a plus rien d'autre). Les gammes, et les references dans
 * chacune, gardent l'ordre de la premiere lecture ; ce qui est arrive depuis passe apres, dans
 * l'ordre du backend (le tri est stable).
 */
export function stabiliser(
  gammes: readonly GammeVue[],
  retenues: ReadonlyMap<string, ReferenceVue>,
  figee: Pick<Depart, "rangs" | "rangsGammes">,
): GammeVue[] {
  const resultat = gammes.map((gamme) => ({ ...gamme, references: [...gamme.references] }));
  for (const reference of retenues.values()) {
    let gamme = resultat.find((g) => g.marque === reference.marque && g.gamme === reference.gamme);
    if (gamme === undefined) {
      gamme = { marque: reference.marque, gamme: reference.gamme, bobines: 0, references: [] };
      resultat.push(gamme);
    }
    gamme.references.push(reference);
  }
  const rang = (rangs: ReadonlyMap<string, number>, cle: string) =>
    rangs.get(cle) ?? Number.MAX_SAFE_INTEGER;
  return resultat
    .map((gamme) => ({
      ...gamme,
      references: gamme.references.sort(
        (a, b) => rang(figee.rangs, a.cle) - rang(figee.rangs, b.cle),
      ),
    }))
    .sort((a, b) => rang(figee.rangsGammes, cleGamme(a)) - rang(figee.rangsGammes, cleGamme(b)));
}

/**
 * Les sept colonnes du bureau : pastille, couleur, jauges, etats, restant, reserve, minimum. Les
 * deux compteurs ont 104 px : sous `md`, leurs boutons font 34 px (100 px le compteur). La
 * colonne de la reserve a ete prise sur les jauges (100 px, six bobines par rang) et sur les
 * etats : a 1 440 px, « Bambu Green » tient encore sur une ligne.
 */
const COLONNES_LARGES =
  "@2xl:grid-cols-[22px_minmax(0,1fr)_100px_minmax(0,1.3fr)_72px_104px_104px]";

export function CarteStock({ vue }: { vue: VueBobines }) {
  const [filtre, setFiltre] = useState("");
  // Ce que Nicolas a ouvert ou ferme a la main ; oublie des que le filtre change.
  const [choix, setChoix] = useState<Readonly<Record<string, boolean>>>({});
  const [figee] = useState(() => depart(vue.gammes));
  // La vue d'avant, pour voir ce que la relecture fait disparaitre (etat derive des props,
  // ajuste pendant le rendu comme React le recommande).
  const [lue, setLue] = useState(vue);
  const [retenues, setRetenues] = useState<ReadonlyMap<string, ReferenceVue>>(new Map());
  if (lue !== vue) {
    setRetenues(retenir(lue, vue, retenues));
    setLue(vue);
  }
  const gammes = useMemo(
    () => stabiliser(vue.gammes, retenues, figee),
    [vue.gammes, retenues, figee],
  );
  const groupes = useMemo(() => groupesDuStock(gammes), [gammes]);
  const filtreActif = filtre.trim() !== "";
  const visibles = groupes.flatMap((tout) => {
    const montre = filtreActif ? filtrer(tout, filtre) : tout;
    return montre === null ? [] : [{ tout, montre }];
  });

  // « Ajouter une couleur en reserve » : ferme, le focus revient a son bouton.
  const [ajout, setAjout] = useState(false);
  const boutonAjout = useRef<HTMLButtonElement>(null);
  const idAjout = useId();
  const fermerAjout = useCallback(() => {
    setAjout(false);
    boutonAjout.current?.focus();
  }, []);
  // La couleur mise en reserve apparait dans son groupe, ouvert pour qu'on la voie.
  const ajoutee = useCallback((couleur: Pick<ReferenceVue, "marque" | "gamme">) => {
    setChoix((avant) => ({ ...avant, [groupeDe(couleur)]: true }));
  }, []);

  return (
    <section aria-labelledby="bobines-stock" className={cn(CARTE, "@container")}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h2 className="text-[15px] font-semibold" id="bobines-stock">
          Stock
        </h2>
        <BoutonAjoutReserve
          basculer={() => (ajout ? fermerAjout() : setAjout(true))}
          boutonRef={boutonAjout}
          idChoix={idAjout}
          ouvert={ajout}
        />
      </div>
      {ajout ? (
        <ChoixReserve ajoutee={ajoutee} fermer={fermerAjout} gammes={gammes} id={idAjout} />
      ) : null}
      <div className="mt-3 mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <input
          aria-label="Filtrer le stock"
          className={cn(
            "h-10 w-full rounded-lg border border-input bg-background px-3 text-sm placeholder:text-muted-foreground @2xl:w-[330px]",
            FOCUS,
          )}
          onChange={(evenement) => {
            setFiltre(evenement.target.value);
            setChoix({});
          }}
          placeholder="Filtrer : noir, PETG, Matte..."
          type="search"
          value={filtre}
        />
        <Legende presqueVideG={vue.seuils.presque_vide_g} />
      </div>
      <p className="mb-1.5 text-[13px] text-muted-foreground">{LEGENDE_RESERVE}</p>
      {visibles.length === 0 ? (
        <SansResultat filtre={filtreActif ? filtre.trim() : null} />
      ) : (
        <ul>
          {visibles.map(({ tout, montre }) => {
            const ouvert = choix[tout.cle] ?? (filtreActif || figee.ouverts.has(tout.cle));
            return (
              <Groupe
                basculer={() => setChoix((avant) => ({ ...avant, [tout.cle]: !ouvert }))}
                key={tout.cle}
                montre={montre}
                ouvert={ouvert}
                tout={tout}
              />
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** Maquette `BobinesEtats.dc.html`, etat 9 ; ou un Filament Manager vide. */
function SansResultat({ filtre }: { filtre: string | null }) {
  if (filtre === null) {
    return <p className="py-3 text-sm">Le Filament Manager ne compte aucune bobine.</p>;
  }
  return (
    <div className="flex flex-col gap-1.5 py-3">
      <p className="text-sm">
        Aucune couleur ni gamme ne contient «{"\u00a0"}
        {filtre}
        {"\u00a0"}».
      </p>
      <p className="text-[13px] text-muted-foreground">
        Le filtre cherche dans la gamme, la couleur et la marque.
      </p>
    </div>
  );
}

/** La legende des jauges, sur la carte large seulement (maquette du bureau). */
function Legende({ presqueVideG }: { presqueVideG: number }) {
  const exemples = [
    { etat: "neuve", restant_g: 1000, texte: "neuve" },
    { etat: "entamee", restant_g: 550, texte: "entamée" },
    { etat: "presque_vide", restant_g: 120, texte: `presque vide, ${presqueVideG} g ou moins` },
    { etat: "finie", restant_g: 0, texte: "finie" },
  ] as const;
  return (
    <ul className="hidden flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-muted-foreground @2xl:flex">
      {exemples.map((exemple) => (
        <li className="inline-flex items-center gap-1.5" key={exemple.etat}>
          <Jauge
            bobine={{ restant_g: exemple.restant_g, depart_g: 1000, etat: exemple.etat }}
            hex="737373"
            petite
          />
          {exemple.texte}
        </li>
      ))}
    </ul>
  );
}

function Groupe({
  tout,
  montre,
  ouvert,
  basculer,
}: {
  /** Le groupe entier : ses comptes et ses teintes ne dependent pas du filtre. */
  tout: GroupeStock;
  /** Ce qui passe le filtre. */
  montre: GroupeStock;
  ouvert: boolean;
  basculer: () => void;
}) {
  const idCorps = useId();
  const references = tout.gammes.flatMap((gamme) => gamme.references);
  const bobines = compter(
    tout.gammes.reduce((total, gamme) => total + gamme.bobines, 0),
    "bobine",
    "bobines",
  );
  const detail = tout.autres
    ? `${bobines} : ${tout.gammes.map((gamme) => `${gamme.marque} ${gamme.gamme}`).join(", ")}`
    : `${bobines}, ${compter(references.length, "couleur", "couleurs")}`;

  return (
    <li className="mt-1.5 border-t border-border first:mt-0">
      <h3>
        <button
          aria-controls={ouvert ? idCorps : undefined}
          aria-expanded={ouvert}
          className={cn(
            "flex w-full flex-wrap items-center gap-x-2.5 gap-y-1 rounded-md py-3.5 text-left",
            FOCUS,
          )}
          onClick={basculer}
          type="button"
        >
          <Chevron ouvert={ouvert} />
          <span className="text-[15px] font-semibold">{tout.titre}</span>
          <span className="text-[13px] font-normal text-muted-foreground">
            <span className="hidden @md:inline">{detail}</span>
            <span className="@md:hidden">{bobines}</span>
          </span>
          {ouvert ? null : (
            <span aria-hidden="true" className="inline-flex flex-wrap gap-1">
              {references.map((reference) => (
                <Pastille hex={reference.couleur_hex} key={reference.cle} petite />
              ))}
            </span>
          )}
        </button>
      </h3>
      {ouvert ? (
        <div className="pb-2" id={idCorps}>
          <div
            aria-hidden="true"
            className={cn(
              "hidden gap-3 pb-1 text-[11.5px] font-semibold tracking-[0.04em] text-muted-foreground uppercase @2xl:grid",
              COLONNES_LARGES,
            )}
          >
            <span />
            <span>Couleur</span>
            <span>Bobines</span>
            <span>État</span>
            <span className="text-right">Restant</span>
            <span className="text-right">{COMPTEURS.reserve.etiquette}</span>
            <span className="text-right">{COMPTEURS.minimum.etiquette}</span>
          </div>
          {montre.gammes.map((gamme) => (
            <div key={cleGamme(gamme)}>
              {tout.autres ? (
                <h4 className="pt-2 pb-1 text-[13px] font-semibold">
                  {gamme.marque} {gamme.gamme}
                </h4>
              ) : null}
              <ul>
                {gamme.references.map((reference) => (
                  <LigneReference key={reference.cle} reference={reference} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Un compteur de la ligne, avec son nom quand la ligne est etroite : au-dessus de lui sur la
 * carte la plus etroite, devant lui au-dela. Sur la carte large, l'en-tete des colonnes les
 * nomme. Le nom affiche est cache au lecteur d'ecran : le groupe porte deja le nom complet
 * (« Bobines en réserve de Black, PLA Basic »).
 */
function CompteurNomme({
  quoi,
  nom,
  etat,
  className,
}: {
  quoi: Reglage;
  nom: string;
  etat: EtatCompteur;
  className: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-start gap-0.5 @xs:flex-row @xs:items-center @xs:justify-end @xs:gap-1.5",
        className,
      )}
    >
      <span aria-hidden="true" className="text-[12px] text-muted-foreground @2xl:hidden">
        {COMPTEURS[quoi].etiquette}
      </span>
      <Compteur etat={etat} nom={nom} quoi={quoi} />
    </div>
  );
}

function LigneReference({ reference }: { reference: ReferenceVue }) {
  const [depliee, setDepliee] = useState(false);
  const idDetail = useId();
  const reserve = useCompteur("reserve", reference);
  const minimum = useCompteur("minimum", reference);
  const enCours = reserve.enCours || minimum.enCours;
  const refus = [reserve.refus, minimum.refus].filter((texte) => texte !== null);
  const aDesBobines = reference.bobines.length > 0;
  const ouverte = depliee && aDesBobines;
  // « Black » existe dans plusieurs gammes : chaque nom accessible porte aussi la sienne.
  const nom = nomComplet(reference);

  return (
    <li className="border-t border-border/60">
      <div
        className={cn(
          "grid grid-cols-[22px_minmax(0,1fr)] items-center gap-x-2.5 gap-y-1 py-2 @xs:grid-cols-[22px_minmax(0,1fr)_auto] @2xl:gap-x-3 @2xl:gap-y-0.5",
          COLONNES_LARGES,
          ouverte && "-mx-1.5 rounded-t-lg bg-muted/60 px-1.5",
        )}
      >
        {/* Trois dispositions, par la largeur de la carte. Sous 20rem (un telephone de 320 ou
            360 px), les deux compteurs ont leur propre rang sous le nom, chacun son nom
            au-dessus : a cote d'eux, la colonne du nom n'avait que 83 px, et les vrais noms
            francais se coupaient au milieu d'un mot (« crépuscul/aire », « Changem/ent »). De
            20 a 42rem, ils sont l'un sous l'autre a droite, leur nom devant eux ; au-dela, une
            colonne chacun. Chaque case pose sa place par les raccourcis `grid-column` et
            `grid-row` : un `row-span` (raccourci) melange a un `row-start` (propriete longue)
            laissait la variante large sans ligne, et la case partait a la ligne. Les deux
            enveloppes en `@2xl:contents` (jauges et etats courts, puis les compteurs) ne servent
            qu'a la ligne etroite : sur la carte large, leurs enfants prennent chacun leur
            colonne. */}
        <Pastille
          className="col-[1] row-[1/span_2] mt-0.5 self-start @2xl:row-[1] @2xl:mt-0 @2xl:self-center"
          hex={reference.couleur_hex}
        />
        <div className="col-[2] row-[1] flex min-w-0 flex-wrap items-baseline gap-x-2 @2xl:col-[2] @2xl:flex-col @2xl:gap-0">
          {aDesBobines ? (
            <button
              aria-controls={ouverte ? idDetail : undefined}
              aria-expanded={ouverte}
              aria-label={nom}
              className={cn(
                "inline-flex min-w-0 items-center gap-1.5 rounded-sm text-left text-sm font-medium [overflow-wrap:anywhere]",
                FOCUS,
              )}
              onClick={() => setDepliee(!depliee)}
              type="button"
            >
              {reference.couleur_nom}
              <Chevron ouvert={ouverte} />
            </button>
          ) : (
            <span className="text-sm font-medium [overflow-wrap:anywhere]">
              {reference.couleur_nom}
            </span>
          )}
          {reference.a_racheter > 0 ? (
            <span className="text-[12.5px] font-medium text-racheter">
              {aRacheterEnClair(reference.a_racheter)}
            </span>
          ) : null}
        </div>
        <div className="col-[2] row-[2] flex min-w-0 flex-wrap items-end gap-x-2 gap-y-0.5 @2xl:contents">
          <span
            aria-hidden="true"
            className="flex flex-wrap items-end gap-1 @2xl:col-[3] @2xl:row-[1]"
          >
            {aDesBobines ? (
              reference.bobines.map((bobine) => (
                <Jauge bobine={bobine} hex={reference.couleur_hex} key={bobine.id} />
              ))
            ) : (
              <Jauge bobine={null} hex={reference.couleur_hex} />
            )}
          </span>
          <p className="min-w-0 text-[12.5px] text-muted-foreground tabular-nums @2xl:hidden">
            {enCours ? <Enregistrement /> : etatsCourts(reference)}
          </p>
        </div>
        <p className="hidden text-[13px] text-muted-foreground @2xl:col-[4] @2xl:row-[1] @2xl:block">
          {enCours ? <Enregistrement /> : etatsEnClair(reference)}
        </p>
        <p className="hidden text-right text-sm tabular-nums @2xl:col-[5] @2xl:row-[1] @2xl:block">
          {grammes(reference.restant_g)}
        </p>
        <div className="col-[2] row-[3] flex flex-wrap items-end gap-x-3 gap-y-1 @xs:col-[3] @xs:row-[1/span_2] @xs:flex-col @xs:flex-nowrap @xs:gap-1 @xs:justify-self-end @2xl:contents">
          <CompteurNomme
            className="@2xl:col-[6] @2xl:row-[1] @2xl:justify-self-end"
            etat={reserve}
            nom={nom}
            quoi="reserve"
          />
          <CompteurNomme
            className="@2xl:col-[7] @2xl:row-[1] @2xl:justify-self-end"
            etat={minimum}
            nom={nom}
            quoi="minimum"
          />
        </div>
        {refus.length === 0 ? null : (
          <div className="col-[2/-1] row-[4] pt-1 text-[13px] text-destructive @xs:row-[3] @2xl:row-[2]">
            {refus.map((texte) => (
              <p key={texte} role="alert">
                {texte}
              </p>
            ))}
          </div>
        )}
      </div>
      {ouverte ? <DetailBobines id={idDetail} nom={nom} reference={reference} /> : null}
    </li>
  );
}

/** Les bobines d'une reference, une par une : jauge, grammes, etat, emplacement. */
function DetailBobines({
  id,
  nom,
  reference,
}: {
  id: string;
  nom: string;
  reference: ReferenceVue;
}) {
  return (
    <ul
      aria-label={`Bobines de ${nom}`}
      className="-mx-1.5 mb-1 rounded-b-lg bg-muted/60 pr-3 pb-2.5 pl-[38px] @2xl:pl-10"
      id={id}
    >
      {reference.bobines.map((bobine) => (
        <li
          className="grid grid-cols-[14px_4.5rem_minmax(0,1fr)] items-center gap-2.5 border-t border-dashed border-border py-1.5 text-[12.5px] first:border-t-0 @2xl:grid-cols-[18px_90px_110px_minmax(0,1fr)] @2xl:gap-3 @2xl:text-[13px]"
          key={bobine.id}
        >
          <Jauge bobine={bobine} hex={reference.couleur_hex} petite />
          <span className="tabular-nums">{grammes(bobine.restant_g)}</span>
          <span className="min-w-0 [overflow-wrap:anywhere]">
            {etatEnClair(bobine.etat)}
            <span className="@2xl:hidden">
              {bobine.emplacement === null ? "" : `, ${bobine.emplacement}`}
            </span>
          </span>
          <span className="hidden min-w-0 text-muted-foreground [overflow-wrap:anywhere] @2xl:block">
            {bobine.emplacement ?? "sans emplacement"}
          </span>
        </li>
      ))}
    </ul>
  );
}
